import {
  Box,
  Column,
  Columns,
  Divider,
  Inline,
  Stack,
} from 'braid-design-system';

import {
  DateRangePicker,
  useDateRangeState,
} from '#src/components/DateRangePicker/DateRangePicker.tsx';
import {
  EventTypePicker,
  useEventTypeState,
} from '#src/components/EventTypePicker/EventTypePicker.tsx';
import { InfiniteScrollingList } from '#src/components/InfiniteScrollingList/InfiniteScrollingList.tsx';

import {
  PaginationDirectionPicker,
  usePaginationDirectionState,
} from '#src/components/PaginationDirectionPicker/PaginationDirectionPicker.tsx';
import { Query } from '#src/components/Query/Query.tsx';
import { useEnvironmentConfig } from '#src/hooks/environment.tsx';
import type {
  PollEventsQuery,
  PollEventsQueryVariables,
} from '#src/types/generated/graphql.ts';

import { Event } from './Event.tsx';
import { POLL_EVENTS } from './queries.ts';

export const EventList = () => {
  const dateRangeState = useDateRangeState();
  const eventTypeState = useEventTypeState();
  const paginationDirectionState = usePaginationDirectionState();

  const { seekAnzSchemeId } = useEnvironmentConfig();

  const { afterDateTime, beforeDateTime } = dateRangeState;
  const { eventTypeCodes } = eventTypeState;
  const { firstOrLast, processPageInfo } = paginationDirectionState;

  return (
    <Stack space="small">
      <Columns space="large" collapseBelow="desktop" alignY="center">
        <Column>
          <Inline alignY="center" collapseBelow="tablet" space="large">
            <EventTypePicker {...eventTypeState} align="right" />

            <PaginationDirectionPicker {...paginationDirectionState} />
          </Inline>
        </Column>
        <Column width="content">
          <DateRangePicker {...dateRangeState} idPrefix="event" />
        </Column>
      </Columns>

      <Divider />

      <Query<PollEventsQuery, PollEventsQueryVariables>
        errorMessage="We couldn’t load your events."
        query={POLL_EVENTS}
        variables={{
          [firstOrLast]: 10,
          filter: {
            afterDateTime: afterDateTime?.toISOString(),
            beforeDateTime: beforeDateTime?.toISOString(),
            eventTypeCodes,
          },
          schemeId: seekAnzSchemeId,
        }}
        notifyOnNetworkStatusChange={false}
      >
        {({ data, fetchMore }) => {
          const { edges, pageInfo } = data.events;

          const { shouldLoadMore, variables } = processPageInfo(pageInfo);

          const loadMore = () => fetchMore({ variables });

          return (
            <InfiniteScrollingList
              loadMore={loadMore}
              shouldLoadMore={shouldLoadMore}
              isEmpty={edges.length === 0}
              subject="events"
              items={edges.map(({ node }) => (
                <Event event={node} key={node.id.value} />
              ))}
              Wrapper={Box}
            />
          );
        }}
      </Query>
    </Stack>
  );
};
